import React, { useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { closeModal, openModal } from "react-url-modal";
import { format } from "date-fns";
import { Modal, Button, TextareaInput } from "@/components";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  fetchCreateTournament,
  fetchGetCreatedTournaments,
  fetchGetAvailableTournaments,
} from "@/redux/tournaments";
import { getProfileInfo } from "@/redux/profile/settings/getProfileInfo/selectors";
import { ICreateTournamentData } from "@/redux/tournaments/types";
import { DateNavigation } from "@/pages/Games/modals/CreateNewGameModal/components/DateNavigation";
import { WeekDays } from "@/pages/Games/modals/CreateNewGameModal/components/WeekDays";
import { TimeTable } from "@/pages/Games/modals/CreateNewGameModal/components/TimeTable";
import { CheckboxGroup } from "@/pages/Games/modals/CreateNewGameModal/components/CheckboxGroup";
import { TournamentTypeSelect } from "./components/TournamentTypeSelect";
import { MaxPlayersInput } from "./components/MaxPlayersInput";
import styles from "./CreateNewTournamentModal.module.scss";
import { phoneMaskFormat } from "@/utils/form/phoneMask";
import { RulesAndPolicyText } from "@/components/RulesAndPolicyText"

interface CreateNewTournamentModalProps {
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  params?: Record<string, any>;
}

const LEVEL_OPTIONS = [
  "1.0",
  "1.5",
  "2.0",
  "2.5",
  "3.0",
  "3.5",
  "4.0",
  "4.5",
  "5.0",
  "5.5",
  "6.0",
  "6.5",
  "7.0",
].map((v) => ({ label: v, value: v }));

const CreateNewTournamentModalComponent = ({
  setIsOpen,
  params = {},
}: CreateNewTournamentModalProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const profileInfo = useAppSelector(getProfileInfo);

  const [selectedDay, setSelectedDay] = useState<string>(() => {
    const initialDate = format(new Date(), "yyyy-MM-dd");
    console.log("[CreateNewTournamentModal] Initial selectedDay:", initialDate);
    return initialDate;
  });
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>(["1.0"]);
  const [tournamentType, setTournamentType] = useState<string>("Мексикано");
  const [entryFee, setEntryFee] = useState<string>("");
  const [maxPlayers, setMaxPlayers] = useState<string>("");

  const organizerName = profileInfo.profileInfo?.name || "";
  const organizerPhone = profileInfo.profileInfo?.phone || "";

  const initialValues = {
    description: "",
    entryFee: "",
    maxPlayers: "",
  };

  const validationSchema = Yup.object({
    entryFee: Yup.string()
      .required("Введите взнос")
      .test("entry-fee", "Взнос должен быть больше 0", (value) => {
        const num = Number(value?.replace(/[^0-9]/g, ""));
        return num > 0;
      }),
    maxPlayers: Yup.string()
      .required("Введите максимальное количество игроков")
      .test("max-players", "Количество должно быть больше 0", (value) => {
        const num = Number(value?.replace(/[^0-9]/g, ""));
        return num > 0;
      }),
    description: Yup.string().max(
      300,
      "Описание должно быть до 300 символов"
    ),
  });

  const handleClose = () => {
    closeModal();
    if (setIsOpen) {
      setIsOpen(false);
    }
  };

  const handleLevelsChange = (values: string[]) => {
    let newValues = values;
    if (values.length > 2) {
      const added = values.find((v) => !selectedLevels.includes(v));
      if (added) {
        newValues = [...selectedLevels, added].slice(-2);
      } else {
        newValues = values.slice(0, 2);
      }
    }
    setSelectedLevels(newValues);
  };

  const handleSubmit = async (
    values: typeof initialValues,
    { setSubmitting }: any
  ) => {
    if (selectedTimes.length === 0 || selectedLevels.length === 0) {
      setSubmitting(false);
      return;
    }

    try {
      const sortedTimes = [...selectedTimes].sort();
      const startTime = sortedTimes[0];
      const endTime = `${sortedTimes[sortedTimes.length - 1].split(":")[0]}:59`;

      const numericLevels = selectedLevels.map((l) => Number(l)).sort((a, b) => a - b);
      const levelFrom = numericLevels[0] ?? Number("1.0");
      const levelTo = numericLevels.length > 1 ? numericLevels[1] : null;

      const data: ICreateTournamentData = {
        tournamentType: tournamentType,
        date: selectedDay,
        startTime: startTime,
        endTime: endTime,
        levelFrom: levelFrom,
        levelTo: levelTo,
        entryFee: Number(values.entryFee.replace(/[^0-9]/g, "")),
        maxPlayers: values.maxPlayers.replace(/[^0-9]/g, "")
          ? Number(values.maxPlayers.replace(/[^0-9]/g, ""))
          : null,
        organizerName: organizerName,
        organizerPhone: `+${organizerPhone.replace(/[^0-9]/g, "")}`,
        description: values.description.trim() || undefined,
      };

      await dispatch(fetchCreateTournament(data)).unwrap();

      // Обновляем списки турниров
      await Promise.all([
        dispatch(fetchGetCreatedTournaments()),
        dispatch(fetchGetAvailableTournaments()),
      ]);

      handleClose();
      openModal({ name: "successCreateTournament" });
    } catch (error) {
      console.error("Error creating tournament:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Создать турнир"
      isOpen={true}
      setIsOpen={setIsOpen || (() => {})}
      closeModal={handleClose}
      modalWindowWidth={600}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnChange={true}
        validateOnBlur={true}
      >
        {(formik) => {
          const isDisabled =
            !selectedDay ||
            selectedTimes.length === 0 ||
            selectedLevels.length === 0 ||
            tournamentType.length === 0 ||
            !formik.values.entryFee ||
            Number(formik.values.entryFee.replace(/[^0-9]/g, "")) === 0 ||
            !formik.values.maxPlayers ||
            Number(formik.values.maxPlayers.replace(/[^0-9]/g, "")) === 0;

          const isFormDisabled = isDisabled || formik.isSubmitting;

          return (
            <Form className={styles.form}>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  Дата и время (Выберите интервал)
                </h3>
                <DateNavigation
                  date={selectedDay}
                  onDateChange={(newDate) => {
                    console.log("[CreateNewTournamentModal] DateNavigation changed:", newDate);
                    setSelectedDay(newDate);
                    setSelectedTimes([]);
                  }}
                />
                <WeekDays
                  date={selectedDay}
                  selectedDate={selectedDay}
                  onDayClick={(dateString) => {
                    console.log("[CreateNewTournamentModal] WeekDays day clicked:", dateString, "current selectedDay:", selectedDay);
                    setSelectedDay(dateString);
                    setSelectedTimes([]);
                  }}
                />
                <div className={styles.timeContainer}>
                  <TimeTable
                    selectedDay={selectedDay}
                    onTimeChange={setSelectedTimes}
                  />
                </div>
              </div>

              <div className={styles.section}>
                <div className={styles.sectionTitle}>Тип турнира</div>
                <TournamentTypeSelect
                  value={tournamentType}
                  onChange={setTournamentType}
                />
              </div>

              <div className={styles.section}>
                <div className={styles.sectionTitle}>
                  Уровень (выберите интервал или минимальный уровень)
                </div>
                <CheckboxGroup
                  selectedValues={selectedLevels}
                  onSelectionChange={handleLevelsChange}
                  options={LEVEL_OPTIONS}
                  columns={2}
                  maxSelected={2}
                  hideInfo
                />
              </div>

              <div className={styles.section}>
                <div className={styles.inputSection}>
                  <label className={styles.label}>Взнос с человека</label>
                  <input
                    type="text"
                    name="entryFee"
                    value={formik.values.entryFee}
                    onChange={(e) => {
                      const newValue = e.target.value.replace(/[^0-9]/g, "");
                      formik.setFieldValue("entryFee", newValue);
                    }}
                    onBlur={formik.handleBlur}
                    placeholder="Введите сумму"
                    className={styles.input}
                    maxLength={6}
                  />
                  <ErrorMessage
                    component="div"
                    className={styles.errorMessage}
                    name="entryFee"
                  />
                </div>

                <div className={styles.inputSection}>
                  <MaxPlayersInput
                    name="maxPlayers"
                    value={formik.values.maxPlayers}
                    onChange={(value) => {
                      formik.setFieldValue("maxPlayers", value);
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.maxPlayers && formik.errors.maxPlayers
                        ? formik.errors.maxPlayers
                        : undefined
                    }
                  />
                  <ErrorMessage
                    component="div"
                    className={styles.errorMessage}
                    name="maxPlayers"
                  />
                </div>

                <div className={styles.sectionTitle}>Информация</div>

                <div className={styles.inputSection}>
                  <div className={styles.disabledInputWrapper}>
                    <label className={styles.disabledLabel}>Номер для связи с Вами</label>
                    <input
                      type="tel"
                      value={phoneMaskFormat(organizerPhone)}
                      disabled
                      className={styles.disabledInput}
                    />
                  </div>
                </div>

                <div className={styles.inputSection}>
                  <div className={styles.disabledInputWrapper}>
                    <label className={styles.disabledLabel}>Имя организатора</label>
                    <input
                      type="text"
                      value={organizerName}
                      disabled
                      className={styles.disabledInput}
                    />
                  </div>
                </div>

                <div className={styles.inputSection}>
                  <div className={styles.textareaWrapper}>
                    <TextareaInput
                      name="description"
                      placeholder="Описание"
                      className={styles.textarea}
                    />
                    <div className={styles.commentInfo}>
                      <span
                        className={
                          formik.values.description.length === 300
                            ? styles.commentWarning
                            : ""
                        }
                      >
                        {formik.values.description.length}/300
                      </span>
                    </div>
                  </div>
                  {formik.values.description.length === 300 && (
                    <div className={styles.commentWarningText}>
                      Текст должен быть до 300 символов
                    </div>
                  )}
                  <ErrorMessage
                    component="div"
                    className={styles.errorMessage}
                    name="description"
                  />
                </div>
              </div>

              <RulesAndPolicyText />

              <Button
                type="submit"
                disabled={isFormDisabled}
                newStyles={styles.submitButton}
              >
                {formik.isSubmitting ? "Создание..." : "Создать турнир"}
              </Button>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default CreateNewTournamentModalComponent;
export const CreateNewTournamentModal = CreateNewTournamentModalComponent;
